/* @flow */
import React, {cloneElement, Component} from 'react';
import {findDOMNode} from 'react-dom';
import {isEqual} from 'lodash';

import {PLUS__16, MINUS__16} from '@homeaway/svg-defs';
import Marker from './Marker';
import Overlay from './Overlay';
import MapHit from '../MapHit/MapHit';
import {iconSymbol, mapCentralPinSymbol} from './helpers';
import {trigger} from '../../utils/edapWrapper';
import {getGoogleMapURL} from './helpers';
import LoadingPlaceholder from '../LoadingPlaceholder/LoadingPlaceholder';
import {
    triggerMapHitClickEDAPEvent,
    triggerMarkerClickEDAPEvent
} from '../../utils/hitEdapUtils';

import type {IntlShape} from 'react-intl';
import {type MapViewport, type MapCentralPin} from './Map';
import type {ListingShape as MapHitListingShape} from '../MapHit/MapHit';

declare var google: {
    maps?: {
        event: {
            addListener: Function,
            clearInstanceListeners: Function,
            trigger: Function,
            addListenerOnce: Function,
            addDomListener: Function
        },
        LatLngBounds: Function,
        LatLng: Function,
        ControlPosition: Object,
        Map: Function,
        Size: Function
    }
};

export type GoogleMapState = {
    bounds: MapViewport,
    zoom: number
};

// TODO: Add a GoogleMap type definition
type GoogleMap = {
    fitBounds: (any, number) => ?Object,
    getZoom: () => number,
    controls: Object,
    getBounds: () => {toJSON: () => Object}
};

type Props = {
    allowUpdate: boolean,
    autoSearchEnabled: boolean,
    bounds: MapViewport,
    changeHitPreview: (?string) => *,
    containerElement: React$Element<any>,
    currentHitPrev: string,
    fullLocale: string,
    highlightedPin: ?string,
    intl: IntlShape,
    listings: MapHitListingShape[],
    loading: boolean,
    mapCentralPin?: MapCentralPin,
    mapElement: React$Element<any>,
    mapMarkersClicked: Object,
    onClickMapMarker: (string) => *,
    onIdleChanged: () => *,
    onMapLoad: (zoom: number) => *,
    onMapMove: (GoogleMapState) => *,
    onMapMoveEnd: Function,
    options: Object,
    setMapMoveState: Function,
    shouldFitbounds: boolean,
    showMapSearchButton: () => *,
    siteName: string,
    zoom: number,
    isTouchDevice: boolean,
    page?: number,
    pageSize?: number
};

type State = {
    isLoading: boolean
};

const overlayTopBottomPadding = 10; // pixels by default
const overlayLeftRightPadding = 10; // pixels by default

const pixelOffsetWidth = -123;
const pixelOffsetHeight = -36;

const mapTimeout = 400;

export default class AsyncGoogleMap extends Component<Props, State> {
    fittingBounds: boolean;
    userUpdatedMap: boolean;
    map: GoogleMap;
    mapRef: any;
    overlayPadding: Object;
    resetMarkersTimer: TimeoutID;
    renderMapTimer: TimeoutID;
    windowOrientationChangeTimer: TimeoutID;

    constructor() {
        super();
        this.state = {
            isLoading: true
        };
    }

    componentDidMount() {
        if (this.props.isTouchDevice) {
            window.addEventListener(
                'orientationchange',
                this.handleOrientationChange,
                {
                    passive: true
                }
            );
        }
    }

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        const {allowUpdate: prevAllowUpdate} = this.props;
        const {allowUpdate: nextAllowUpdate} = nextProps;

        if (
            (!prevAllowUpdate && nextAllowUpdate) ||
            (prevAllowUpdate &&
                !nextAllowUpdate &&
                !isEqual(this.props.bounds, nextProps.bounds))
        ) {
            // timeout needed to allow visibility of the map container
            clearTimeout(this.renderMapTimer);
            this.renderMapTimer = setTimeout(
                this.mapResizeAndFitBounds.bind(this),
                mapTimeout
            );
            return true;
        }

        const isStateLoading =
            nextState.isLoading !== this.state.isLoading && nextState.isLoading;

        if (!isStateLoading && nextProps.loading) return false;

        return nextAllowUpdate;
    }

    componentDidUpdate(prevProps: Props) {
        const map = this.map;
        const prevBounds = prevProps.bounds;
        const {bounds, shouldFitbounds} = this.props;
        const boundsEqual = isEqual(prevBounds, bounds);

        if (
            map &&
            !boundsEqual &&
            shouldFitbounds &&
            !this.fittingBounds &&
            !this.userUpdatedMap
        ) {
            this.fitGoogleMapBounds({map, bounds});
        }
    }

    componentWillUnmount() {
        /* istanbul ignore else */
        if (google.maps) {
            google.maps.event.clearInstanceListeners(this.map);
        }
        if (this.props.isTouchDevice) {
            window.removeEventListener(
                'orientationchange',
                this.handleOrientationChange
            );
        }
    }

    initMap = () => {
        if (typeof google === 'undefined' || !google.maps) {
            this.loadMap();
        } else {
            this.setMap();
        }
    };

    fitGoogleMapBounds: ({
        bounds: MapViewport,
        map: GoogleMap
    }) => void = ({
        bounds: {neLat, neLong, swLat, swLong},
        // do not destructure map to {fitBounds};
        // why? - fitBounds acts on the map object
        map
    }) => {
        /* istanbul ignore else */
        if (google.maps) {
            this.fittingBounds = true;
            map.fitBounds(
                new google.maps.LatLngBounds(
                    new google.maps.LatLng(swLat, swLong),
                    new google.maps.LatLng(neLat, neLong)
                ),
                0
            );
        }
    };

    mapTriggerResize() {
        if (google.maps) {
            google.maps.event.trigger(this.map, 'resize');
        }
    }

    mapResizeAndFitBounds() {
        this.mapTriggerResize();
        this.fitGoogleMapBounds({
            bounds: this.props.bounds,
            map: this.map
        });
    }

    onMapMoveEnd = () => {
        this.props.onMapMoveEnd();
        this.props.setMapMoveState(false);
        this.userUpdatedMap = true;
    };

    onMapDragStart = () => {
        clearTimeout(this.resetMarkersTimer);
        this.props.showMapSearchButton();
        this.props.setMapMoveState(true);
        this.userUpdatedMap = true;
    };

    onZoomChange = () => {
        clearTimeout(this.resetMarkersTimer);
        if (!this.fittingBounds) {
            this.userUpdatedMap = true;
        }
        // fitBounds occasionally calls onZoomChange setting false here since its done.
        this.fittingBounds = false;
    };

    onIdleChanged = () => {
        if (this.props.autoSearchEnabled) {
            clearTimeout(this.resetMarkersTimer);
            this.resetMarkersTimer = setTimeout(this.handleIdle, mapTimeout);
        } else {
            this.handleIdle();
        }
    };

    handleOrientationChange = () => {
        clearTimeout(this.windowOrientationChangeTimer);
        this.windowOrientationChangeTimer = setTimeout(() => {
            this.mapTriggerResize();
        }, mapTimeout);
    };

    handleIdle = () => {
        if (this.props.allowUpdate && this.userUpdatedMap) {
            this.props.onMapMove(this.getMapState());
        }

        this.fittingBounds = false;
        this.userUpdatedMap = false;

        this.props.onIdleChanged();
    };

    setListeners = () => {
        // Map tiles have been loaded and Maps is now ready
        const map = this.map;
        const {changeHitPreview} = this.props;

        const {maps} = google;

        /* istanbul ignore next */
        if (maps) {
            maps.event.addListener(map, 'click', changeHitPreview);
            maps.event.addListener(map, 'dragend', this.onMapMoveEnd);
            maps.event.addListener(map, 'dragstart', this.onMapDragStart);
            maps.event.addListener(map, 'idle', this.onIdleChanged);
            maps.event.addListener(map, 'zoom_changed', this.onZoomChange);
        }

        /* The screen size (mobile, desktop) in conjuction with any or no bounding box will return
         * a different zoom number. Syncing the zoom state onMapLoad will prevent uneccesary zooms on pan
         * or on hitClick previews
         */
        let mapZoom = this.map.getZoom();
        this.props.onMapLoad(mapZoom);
        this.userUpdatedMap = false;
        this.fittingBounds = false;
        this.setState({isLoading: false});
    };

    loadMap = () => {
        const map = document.createElement('script');

        map.type = 'text/javascript';
        map.async = true;
        map.defer = true;
        /* istanbul ignore next */
        map.onload = this.setMap;
        map.src = getGoogleMapURL(this.props.intl.locale, this.props.siteName);

        // enzyme ignores overriding the document if you don't mock away
        /* istanbul ignore else */
        if (document && document.body) {
            document.body.appendChild(map);
        }
    };

    setMap = () => {
        // Map is about to load, prevents markers from drawing before
        // map is ready
        this.setState({isLoading: true});

        const {bounds, options, zoom: zoomProp} = this.props;

        const node = findDOMNode(this.mapRef);

        const {maps} = google;

        /* istanbul ignore if */
        if (!maps) {
            return;
        }

        const {zoomControl, ...mapOptions} = Object.assign(
            {
                center: {lat: bounds.neLat, lng: bounds.swLong},
                disableDefaultUI: true,
                draggable: true,
                fullscreenControl: false,
                gestureHandling: 'greedy',
                mapTypeControl: false,
                panControl: false,
                scaleControl: true,
                scrollwheel: false,
                streetViewControl: false,
                zoom: zoomProp,
                zoomControl: true
            },
            options
        );

        this.map = new maps.Map(node, {
            options: mapOptions
        });

        this.overlayPadding = new maps.Size(
            overlayLeftRightPadding,
            overlayTopBottomPadding
        );

        this.fitGoogleMapBounds({map: this.map, bounds});

        /* Google maps will fire listeners, specifically 'zoom_changed' 'center_changed'
        and 'idle' while before the tiles are loaded making the search here button display
        on load so we need to wait to add those listeners after the tiles have loaded */

        maps.event.addListenerOnce(this.map, 'tilesloaded', this.setListeners);

        if (zoomControl) {
            // create custom control
            const zoomControlDiv = this.createZoomControl(this.map);
            this.map.controls[maps.ControlPosition.TOP_LEFT].push(
                zoomControlDiv
            );
        }
    };

    createZoomControl(map: any) {
        const controlDiv = document.createElement('div');

        const controlWrapper = document.createElement('div');
        controlWrapper.classList.add('Map__zoom-wrapper');
        controlWrapper.classList.add('Map__control');
        controlDiv.appendChild(controlWrapper);

        // zoom in
        const zoomInButton = document.createElement('div');
        zoomInButton.className = 'Map__zoom-in';
        controlWrapper.appendChild(zoomInButton);

        // plus icon
        zoomInButton.innerHTML = PLUS__16;

        // divider
        const divider = document.createElement('div');
        divider.className = 'Map__zoom-divider';
        controlWrapper.appendChild(divider);

        // zoom out
        const zoomOutButton = document.createElement('div');
        zoomOutButton.className = 'Map__zoom-out';
        controlWrapper.appendChild(zoomOutButton);

        // minus icon
        zoomOutButton.innerHTML = MINUS__16;

        const {maps} = google;

        /* istanbul ignore else */
        if (maps) {
            maps.event.addDomListener(zoomInButton, 'click', () => {
                map.setZoom(map.getZoom() + 1);
            });
            maps.event.addDomListener(zoomOutButton, 'click', () => {
                map.setZoom(map.getZoom() - 1);
            });
        }

        return controlDiv;
    }

    getMapState(): GoogleMapState {
        const map = this.map;
        const {north, east, south, west} = map.getBounds().toJSON();
        const bounds = {
            neLat: north,
            neLong: east,
            swLat: south,
            swLong: west
        };

        const zoom = map.getZoom();

        return {bounds, zoom};
    }

    handleMapPinClick = () => {
        // Let's raise this event to see if users are interacting with the center pin
        trigger('search.map.pin.click', {});
    };

    handleMarkerClick = (listingIdClicked: string) => {
        const {
            currentHitPrev,
            changeHitPreview,
            onClickMapMarker,
            listings,
            page,
            pageSize
        } = this.props;
        if (currentHitPrev !== listingIdClicked) {
            let hitIndex: number = -1;
            const listing = listings.find(
                ({listingId}: MapHitListingShape, index) => {
                    if (listingId === listingIdClicked) {
                        hitIndex = index;
                        return true;
                    }
                    return false;
                }
            );

            if (listing) {
                triggerMarkerClickEDAPEvent({
                    listing,
                    hitIndex,
                    page,
                    pageSize
                });
            }
            changeHitPreview(listingIdClicked);
            onClickMapMarker(listingIdClicked);
        }
        this.forceUpdate();
    };

    setGoogleMapRef = (ref: ?Object) => {
        this.mapRef = ref;

        if (ref) {
            this.initMap();
        }
    };

    getChildren(listings: MapHitListingShape[], siteName: string) {
        const {
            fullLocale,
            currentHitPrev,
            mapCentralPin,
            mapMarkersClicked,
            highlightedPin,
            page,
            pageSize
        } = this.props;

        const {maps} = google;

        if (!listings || !maps) {
            return [];
        }

        let mapHits: Array<any> = listings
            .map((listing, index) => {
                /* we don't do anything when we can't render hit marker */
                if (
                    !listing ||
                    !listing.geoCode ||
                    !listing.geoCode.latitude ||
                    !listing.geoCode.longitude
                ) {
                    return null;
                }
                const position = new maps.LatLng(
                    // $FlowFixMe
                    listing.geoCode.latitude,
                    // $FlowFixMe
                    listing.geoCode.longitude
                );
                const isHighlighted = listing.listingId === highlightedPin;
                const icon = iconSymbol(
                    siteName,
                    isHighlighted,
                    mapMarkersClicked[listing.listingId]
                );

                return (
                    <Marker
                        key={listing.listingId}
                        icon={icon}
                        listingId={listing.listingId}
                        onClick={this.handleMarkerClick}
                        map={this.map}
                        position={position}
                        isHighlighted={isHighlighted}
                    >
                        {currentHitPrev === listing.listingId && (
                            <Overlay
                                pixelOffset={
                                    new maps.Size(
                                        pixelOffsetWidth,
                                        pixelOffsetHeight
                                    )
                                }
                                infoBoxClearance={this.overlayPadding}
                            >
                                <MapHit
                                    listing={listing}
                                    onHitClick={triggerMapHitClickEDAPEvent}
                                    intl={this.props.intl}
                                    hitIndex={index}
                                    page={page}
                                    pageSize={pageSize}
                                    fullLocale={fullLocale}
                                />
                            </Overlay>
                        )}
                    </Marker>
                );
            })
            .filter((hit) => !!hit);

        if (mapCentralPin) {
            const {lat, lng} = mapCentralPin;
            let mapPinPosition = new maps.LatLng(lat, lng);

            mapHits.push(
                <Marker
                    // Flow ignores check above
                    // $FlowFixMe
                    key={`${lat}:${lng}`}
                    listingId={'-1'}
                    map={this.map}
                    onClick={this.handleMapPinClick}
                    isHighlighted={false}
                    position={mapPinPosition}
                    draggable={false}
                    clickable={false}
                    zIndex={10000000}
                    icon={mapCentralPinSymbol()}
                />
            );
        }

        return mapHits;
    }

    render() {
        if (!this.props.allowUpdate) {
            return null;
        }
        const {listings, siteName, containerElement, mapElement} = this.props;
        const {isLoading} = this.state;
        let children;
        let loadingElement;

        // Loads markers onto the map
        if (isLoading) {
            loadingElement = <LoadingPlaceholder extraClasses="Map--loading" />;
        } else {
            children = this.getChildren(listings, siteName);
        }

        return cloneElement(
            containerElement,
            {},
            cloneElement(
                mapElement,
                {
                    ref: this.setGoogleMapRef
                },
                children
            ),
            loadingElement
        );
    }
}
